// ── Starter onboarding helpers. ──
// Bridges the canonical @mn/content STARTERS (speciesId + element) to the local
// monsters.json roster the battle/party systems consume. The bridge is by display name:
// each starter species' name (e.g. "Emberling") matches a monsters.json `name` entry.

import { STARTERS, BEASTS_BY_ID } from '@mn/content';
import { DATA_ASSET_KEYS } from '../assets/asset-keys.js';
import { hpAtLevel } from '../battle/type-chart.js';

/**
 * @typedef StarterOption
 * @type {object}
 * @property {string} speciesId canonical content id, e.g. "sp_emberling"
 * @property {string} name display name, e.g. "Emberling"
 * @property {'flame'|'tide'|'verdant'} element
 * @property {number} startLevel
 */

/**
 * The three starter choices, resolved to display data from @mn/content. Order matches STARTERS.
 * @returns {StarterOption[]}
 */
export function getStarterOptions() {
  return STARTERS.map((starter) => {
    const beast = BEASTS_BY_ID[starter.speciesId];
    return {
      speciesId: starter.speciesId,
      name: beast?.name ?? starter.speciesId,
      element: starter.element,
      startLevel: starter.startLevel,
    };
  });
}

/**
 * Build a battle-ready Monster instance for the chosen starter from monsters.json.
 * Looks the species up by display name (case-insensitive) so it stays correct if the
 * numeric ids in monsters.json are ever reordered. Returns undefined if unmatched.
 * @param {Phaser.Scene} scene
 * @param {string} speciesId canonical @mn/content id
 * @returns {import('../types/typedef.js').Monster | undefined}
 */
export function buildStarterMonster(scene, speciesId) {
  const beast = BEASTS_BY_ID[speciesId];
  const wantedName = (beast?.name ?? '').toLowerCase();
  if (!wantedName) {
    return undefined;
  }

  /** @type {import('../types/typedef.js').Monster[]} */
  const roster = scene.cache.json.get(DATA_ASSET_KEYS.MONSTERS) || [];
  const match = roster.find((m) => (m.name ?? '').toLowerCase() === wantedName);
  if (!match) {
    return undefined;
  }
  // deep-copy so we never mutate the cached roster entry, then scale HP to the starter's level so a
  // new player's starter matches the level-scaled stats every other monster now uses.
  const starter = JSON.parse(JSON.stringify(match));
  starter.maxHp = hpAtLevel(starter.maxHp, starter.currentLevel);
  starter.currentHp = starter.maxHp;
  return starter;
}
